import React, { useState, useCallback, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    TextField,
    CircularProgress,
    LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PsychologyIcon from "@mui/icons-material/Psychology";
import DownloadIcon from "@mui/icons-material/Download";
import BusinessIcon from "@mui/icons-material/Business";
import SaveIcon from "@mui/icons-material/Save";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UploadExcel from "./UploadExcel";
import AutomationList from "./AutomationList";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import Cookies from "js-cookie";
import useNotification from "../../common/Notification";
import { WB_SX } from "../../../ui/ux/uiTokens";

const AGENT_FAQS_URL = "/whatsapp-business/agentmanager/faqs";

const extractFaqs = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.faqs)) return payload.faqs;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.data?.faqs)) return payload.data.faqs;
    if (Array.isArray(payload.data?.data)) return payload.data.data;
    return [];
};

const toFaqPayload = (row) => ({
    question: String(row.question || row.phrase || "").trim(),
    answer: String(row.answer || row.reply || "").trim(),
    template_id:
        row.template_id != null && row.template_id !== ""
            ? String(row.template_id)
            : "",
    active: true,
});

const fetchAllAutomationRows = async () => {
    const all = [];
    let page = 1;
    let lastPage = 1;
    do {
        const response = await axiosInstance.get("/whatsapp-business/automation/list", {
            params: {
                page,
                per_page: 100,
                sort_by: "id",
                sort_order: "asc",
            },
        });
        if (response.data?.success === false) {
            throw new Error(response.data?.message || "Failed to load records");
        }
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        all.push(...rows);
        lastPage = Number(response.data?.pagination?.last_page) || 1;
        page += 1;
    } while (page <= lastPage);
    return all;
};

const BotTraining = () => {
    const [trainModalOpen, setTrainModalOpen] = useState(false);
    const [businessDetailsModalOpen, setBusinessDetailsModalOpen] = useState(false);
    const [listRefreshTrigger, setListRefreshTrigger] = useState(0);

    // Business details state
    const [businessDetails, setBusinessDetails] = useState(null);
    const [businessDetailsLoading, setBusinessDetailsLoading] = useState(false);
    const [businessDetailInput, setBusinessDetailInput] = useState("");
    const [savingBusinessDetails, setSavingBusinessDetails] = useState(false);
    const [trainedFaqCount, setTrainedFaqCount] = useState(null);
    const [training, setTraining] = useState(false);
    const [trainProgress, setTrainProgress] = useState({
        current: 0,
        total: 0,
        label: "",
    });
    const notify = useNotification();

    const fetchTrainedFaqs = useCallback(async () => {
        try {
            const response = await axiosInstance.get(AGENT_FAQS_URL);
            const faqs = extractFaqs(response.data);
            const total =
                response.data?.pagination?.total ??
                response.data?.meta?.total ??
                response.data?.total ??
                faqs.length;
            setTrainedFaqCount(Number(total) || faqs.length);
            return faqs;
        } catch (error) {
            if (error.response?.status === 404) {
                setTrainedFaqCount(0);
                return [];
            }
            console.error("Error fetching FAQs:", error);
            setTrainedFaqCount(null);
            return [];
        }
    }, []);

    useEffect(() => {
        fetchTrainedFaqs();
    }, [fetchTrainedFaqs]);

    const handleUploadSuccess = useCallback(() => {
        setListRefreshTrigger((prev) => prev + 1);
        setTrainModalOpen(false);
    }, []);

    const handleTrainBot = useCallback(async () => {
        if (training) return;

        setTraining(true);
        setTrainProgress({ current: 0, total: 1, label: "Loading records…" });

        try {
            const rows = await fetchAllAutomationRows();
            const faqs = rows.map(toFaqPayload).filter((faq) => faq.question && faq.answer);

            if (faqs.length === 0) {
                notify("No phrase & reply records to sync. Upload Excel first.", "warning");
                return;
            }

            const total = faqs.length + 1;
            setTrainProgress({
                current: 0,
                total,
                label: "Clearing existing FAQs…",
            });

            try {
                await axiosInstance.delete(AGENT_FAQS_URL);
            } catch (error) {
                if (error.response?.status !== 404) {
                    throw error;
                }
            }
            setTrainProgress({
                current: 1,
                total,
                label: `Syncing FAQ 0 of ${faqs.length}…`,
            });

            let failed = 0;
            for (let i = 0; i < faqs.length; i += 1) {
                setTrainProgress({
                    current: 1 + i,
                    total,
                    label: `Syncing FAQ ${i + 1} of ${faqs.length}…`,
                });
                try {
                    await axiosInstance.post(AGENT_FAQS_URL, {
                        faqs: [faqs[i]],
                    });
                } catch (err) {
                    failed += 1;
                    console.error("Failed to post FAQ", faqs[i], err);
                }
                setTrainProgress({
                    current: 1 + i + 1,
                    total,
                    label: `Uploaded FAQ ${i + 1} of ${faqs.length}`,
                });
            }

            await fetchTrainedFaqs();
            setListRefreshTrigger((prev) => prev + 1);

            if (failed === 0) {
                notify(`Synced ${faqs.length} FAQs with AI Agent`, "success");
            } else {
                notify(
                    `Synced ${faqs.length - failed} of ${faqs.length} FAQs (${failed} failed)`,
                    "error"
                );
            }
        } catch (error) {
            const msg =
                error.response?.data?.message || error.message || "Failed to sync with AI Agent";
            notify(msg, "error");
        } finally {
            setTraining(false);
        }
    }, [fetchTrainedFaqs, notify, training]);

    const handleCloseTrainModal = useCallback(() => {
        setTrainModalOpen(false);
    }, []);

    const fetchBusinessDetails = useCallback(async () => {
        setBusinessDetailsLoading(true);
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/business-details`
            );
            if (response.data?.success) {
                const data = response.data.data;
                setBusinessDetails(data);
                setBusinessDetailInput(data?.business_detail || "");
            } else {
                setBusinessDetails(null);
                setBusinessDetailInput("");
            }
        } catch (error) {
            console.error("Error fetching business details:", error);
            setBusinessDetails(null);
            setBusinessDetailInput("");
        } finally {
            setBusinessDetailsLoading(false);
        }
    }, []);

    const handleOpenBusinessDetailsModal = useCallback(() => {
        setBusinessDetailsModalOpen(true);
        fetchBusinessDetails();
    }, [fetchBusinessDetails]);

    const handleCloseBusinessDetailsModal = useCallback(() => {
        setBusinessDetailsModalOpen(false);
        setBusinessDetails(null);
        setBusinessDetailInput("");
    }, []);

    const handleSaveBusinessDetails = useCallback(async () => {
        const detail = businessDetailInput.trim();
        if (!detail) {
            notify("Please enter business details", "error");
            return;
        }
        const userId = Cookies.get("userId");
        let parsedUserId = userId;
        try {
            parsedUserId = JSON.parse(userId);
        } catch {
            // use as-is if not JSON
        }
        if (!parsedUserId) {
            notify("User session not found. Please log in again.", "error");
            return;
        }
        setSavingBusinessDetails(true);
        try {
            const response = await axiosInstance.put(
                `${base_url}/whatsapp-business/business-details`,
                {
                    user_id: String(parsedUserId),
                    business_detail: detail,
                }
            );
            if (response.data?.success) {
                notify("Business details saved successfully", "success");
                setBusinessDetails(response.data.data);
                fetchBusinessDetails();
            } else {
                notify(response.data?.message || "Failed to save business details", "error");
            }
        } catch (error) {
            const msg = error.response?.data?.message || "Failed to save business details";
            notify(msg, "error");
        } finally {
            setSavingBusinessDetails(false);
        }
    }, [businessDetailInput, fetchBusinessDetails, notify]);

    return (
    <Box sx={{ ...WB_SX.page, p: 2 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 2,
                }}
            >
                <Box>
                    <Typography variant="h5" sx={{ ...WB_SX.pageTitle, mb: trainedFaqCount != null ? 0.5 : 2 }}>
                        AI Agent
                    </Typography>
                    {trainedFaqCount != null && (
                        <Typography variant="body2" color="text.secondary">
                            Trained FAQs: {trainedFaqCount}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        component="a"
                        href="/files/sample_bot_trainging_template2026.xlsx"
                        download="sample_bot_training_template.xlsx"
                        startIcon={<DownloadIcon />}
                        sx={WB_SX.secondaryButton}
                    >
                        Download Sample
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => setTrainModalOpen(true)}
                        sx={WB_SX.secondaryButton}
                    >
                        Upload Excel
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<BusinessIcon />}
                        onClick={handleOpenBusinessDetailsModal}
                    >
                        Business Details
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={
                            training ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <PsychologyIcon />
                            )
                        }
                        onClick={handleTrainBot}
                        disabled={training}
                        sx={WB_SX.primaryButton}
                    >
                        Sync with Agent
                    </Button>
                </Box>
            </Box>

            <AutomationList refreshTrigger={listRefreshTrigger} onRowSynced={fetchTrainedFaqs} />

            <Dialog
                open={trainModalOpen}
                onClose={handleCloseTrainModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
                        overflow: "hidden",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        py: 2,
                        px: 3,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <PsychologyIcon />
                        </Box>
                        <Box>
                            <Typography variant="h6" component="span" fontWeight={600}>
                                Upload Excel
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Upload Excel to add phrase & reply data
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={handleCloseTrainModal}
                        size="small"
                        sx={{ color: "text.secondary" }}
                        aria-label="Close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, "&:first-of-type": { py: 0 } }}>
                    <UploadExcel onUploadSuccess={handleUploadSuccess} />
                </DialogContent>
            </Dialog>

            <Dialog
                open={training}
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown
                onClose={() => {}}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        py: 2,
                        px: 3,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Typography variant="h6" fontWeight={600}>
                        Syncing with Agent
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {trainProgress.label || "Preparing…"}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={
                            trainProgress.total
                                ? Math.round((trainProgress.current / trainProgress.total) * 100)
                                : 0
                        }
                        sx={{
                            height: 10,
                            borderRadius: 2,
                            backgroundColor: "rgba(0,0,0,0.06)",
                            "& .MuiLinearProgress-bar": {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        {trainProgress.total
                            ? `${Math.round((trainProgress.current / trainProgress.total) * 100)}%`
                            : "0%"}
                    </Typography>
                </DialogContent>
            </Dialog>

            {/* Business Details Modal */}
            <Dialog
                open={businessDetailsModalOpen}
                onClose={handleCloseBusinessDetailsModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        py: 2.5,
                        px: 4,
                        borderBottom: 1,
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <BusinessIcon sx={{ fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" component="span" fontWeight={600}>
                                Business Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                Provide context for the bot to understand your business
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={handleCloseBusinessDetailsModal}
                        size="medium"
                        sx={{
                            color: "text.secondary",
                            "&:hover": { backgroundColor: "action.hover" },
                        }}
                        aria-label="Close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, "&:first-of-type": { py: 0 } }}>
                    {businessDetailsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8, px: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                            <Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                    <DescriptionIcon sx={{ fontSize: 20, color: "primary.main" }} />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Describe your products, services, and key info
                                    </Typography>
                                </Box>
                                <TextField
                                    placeholder="e.g., We sell electronics and gadgets. Our store is located in Mumbai. Contact us for wholesale prices..."
                                    multiline
                                    rows={7}
                                    value={businessDetailInput}
                                    onChange={(e) => setBusinessDetailInput(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            padding: 1.5,
                                        },
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveBusinessDetails}
                                    disabled={savingBusinessDetails}
                                    startIcon={savingBusinessDetails ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                    sx={{ px: 4, py: 1.5 }}
                                >
                                    {businessDetails ? "Update" : "Add"} Business Details
                                </Button>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default BotTraining;
